<template>
    <Modal class="partial-payment-modal"
           size="sm"
           icon="fa-input-numeric"
           :ok="ok"
           :ok-enabled="okEnabled"
           :cancel="cancel"
           :custom-title-text="titleText">
        <div class="form-v">
            <div class="cash-labels mb-1">
                <div v-if="isRefund"
                     class="alert alert-danger cash-label">
                    {{ l('PAYMENT.REMAINING_REFUND') }}
                    <h3>{{ currencyFilter(Math.abs(remainingBalance)) }}</h3>
                </div>
                <div v-else
                     class="alert alert-info cash-label">
                    {{ l('PAYMENT.REMAINING_BALANCE_DUE') }}
                    <h3>{{ currencyFilter(remainingBalance) }}</h3>
                </div>

                <div class="alert alert-success cash-label">
                    {{ l('PAYMENT.TENDERED') }}
                    <h3>{{ currencyFilter(form.value) }}</h3>
                </div>
            </div>

            <NumPad v-model="form.value"
                    :show-value="false"
                    :show-sign-button="false"
                    :force-negative="isRefund"
                    @ok="ok"
                    @cancel="cancel">
                <template v-if="showSplitBtn"
                          v-slot:btn>
                    <a class="btn btn-info btn-lg btn--flex"
                       @click.prevent="openSplitModal">
                        <i class="fa-solid fa-hexagon-divide"></i>
                        {{ l('PAYMENT.SPLIT__BTN') }}
                    </a>
                </template>
            </NumPad>

            <TransactionRefInput ref="transaction-ref"
                                 v-model="form.transactionRef"
                                 :payment-method="paymentMethod"
                                 @enter="ok" />
        </div>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import NumPad from '@/vue/components/NumPad/NumPad';
import TransactionRefInput from '@/epos/vue/modals/Payment/components/TransactionRefInput';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

export default {
    components: {
        TransactionRefInput,
        NumPad,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        // Is this modal being shown before payment is taken to set the amount to take ("make"),
        // os is it being shown after an amount was already taken ("received").
        mode: {
            type: String,
            default: 'make',
        },

        isRefund: {
            type: Boolean,
            default: false,
        },

        orderTotal: {
            type: Number,
            required: true,
        },

        remainingBalance: {
            type: Number,
            required: true,
        },

        maxPayment: {
            type: Number,
            required: true,
        },

        paymentMethod: {
            type: Object,
            required: true,
        },

        showTransactionRef: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            form: {
                value: 0.00,
                transactionRef: null,
            },
        };
    },

    computed: {
        showSplitBtn() {
            return !this.isRefund;
        },

        titleText() {
            return container.langService.getText(
                this.titleTextKey,
                {
                    paymentMethod: this.paymentMethod.shortName,
                }
            );
        },

        titleTextKey() {
            if (this.isRefund) {
                if (this.mode === 'received') {
                    return 'PAYMENT.PARTIAL_REFUNDED_MODAL_TITLE';
                }

                return 'PAYMENT.PARTIAL_REFUND_MODAL_TITLE';
            }

            if (this.mode === 'received') {
                return 'PAYMENT.PARTIAL_RECEIVED_MODAL_TITLE';
            }

            return 'PAYMENT.PARTIAL_MAKE_MODAL_TITLE';
        },

        okEnabled() {
            return parseFloat(this.form.value) !== 0.00;
        },
    },

    methods: {
        openSplitModal() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.SPLIT_PAYMENT,
                    {
                        maxPayment: this.maxPayment,
                        orderTotal: this.orderTotal,
                        remainingBalance: this.remainingBalance,
                    }
                )
                .then(({ value }) => {
                    this.form.value = value;
                    // Automatically submit.
                    // this.ok();
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        ok() {
            // Validate the transaction ref if necessary.
            if (this.$refs['transaction-ref']) {
                if (!this.$refs['transaction-ref'].validate()) {
                    this.$refs['transaction-ref'].focus();
                    return;
                }
            }

            if (!this.okEnabled) {
                return;
            }

            if (this.isRefund && this.form.value < this.maxPayment) {
                const errorLangKey = this.mode === 'received' ? 'PAYMENT.MAX_AMOUNT_ALERT--REFUND--RECEIVED' : 'PAYMENT.MAX_AMOUNT_ALERT--REFUND';
                container.toastFactory.errorTop(
                    null,
                    container.langService.getText(errorLangKey, { max: this.maxPayment })
                );
                return;
            } else if (!this.isRefund && this.form.value > this.maxPayment) {
                const errorLangKey = this.mode === 'received' ? 'PAYMENT.MAX_AMOUNT_ALERT--RECEIVED' : 'PAYMENT.MAX_AMOUNT_ALERT';
                container.toastFactory.errorTop(
                    null,
                    container.langService.getText(errorLangKey, { max: this.maxPayment })
                );
                return;
            }

            this.resolve({
                ...this.form,
                paymentMethod: this.paymentMethod,
            });
        },
    },
};
</script>
